import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useAppStore } from '../../app/hooks/useAppStore';
import { ACHIEVEMENTS, AchievementCheckData } from '../progress/AchievementsSection';
import { AchievementUnlockModal, AchievementData } from './AchievementUnlockModal';
import { MODULES } from '../../content';
import { MODULE_MASTERED_THRESHOLD } from '../../config/constants';
import { triggerSkinReveal } from '../../mascot/unlock/useSkinReveal';
import { useDjaySelection } from '../../mascot/useDjaySelection';
import type { DjayVariant } from '../../mascot/DjayAvatar';
// Module-level baseline ledger — survives unmount/remount of this component.
//
// CRITICAL: a fullscreen practice session replaces the entire app shell (see
// AppContent), so this component is UNMOUNTED for the whole session and
// REMOUNTS when the learner returns. A per-mount ref would treat that remount
// as a fresh account load and silently baseline (suppress) any achievement the
// learner just crossed mid-session — e.g. reaching level 10 ("Kampioen") would
// never get its celebration modal. Keying the baseline off a module-level Set
// of account ids means: the FIRST time an account is seen we baseline silently
// (no "5 popups on load"), but a remount within the same session pops genuine
// new unlocks. A hard page reload resets the module, which correctly baselines
// again so we don't re-pop everything on reload.
const baselinedAccounts = new Set<string>();
interface AchievementCeremonyProps {
  /**
   * Wayfinding callback — navigates the learner to the Collection page. When
   * provided, the unlock modal surfaces a "Bekijk in Collectie" action so a
   * just-earned reward is always findable. The Collection deep-focuses the
   * reward via the `collectionFocus` sessionStorage handshake.
   */
  onNavigateToCollection?: () => void;
}
export function AchievementCeremony({
  onNavigateToCollection
}: AchievementCeremonyProps = {}) {
  const {
    state,
    activeAccount,
    getModuleMasteryForGroup,
    getAnsweredQuestions,
    markAchievementUnlocked,
    clearPendingLevelUp
  } = useAppStore();
  const {
    selectedVariant
  } = useDjaySelection();
  const [activeAchievement, setActiveAchievement] = useState<AchievementData | null>(null);
  const [queue, setQueue] = useState<AchievementData[]>([]);
  // When the active achievement grants a Djay skin, defer the cinematic
  // skin-evolution reveal until AFTER the badge modal is dismissed, so the two
  // celebrations read as a sequence rather than stacking. Captured at
  // modal-open so `from` reflects the look the learner had before any equip.
  const pendingSkinRevealRef = useRef<{
    variant: DjayVariant;
    from: DjayVariant;
  } | null>(null);
  // If a level-up is pending when the FIRST achievement of a batch surfaces,
  // we fold the level-up celebration into that modal so the two never stack as
  // competing popups. Captured once per modal-open and cleared on close.
  const [levelUpTo, setLevelUpTo] = useState<number | null>(null);
  // Tracks the account this mount last saw, used only to clear the in-flight
  // queue when the active account changes. The baseline-suppression decision
  // itself lives in the module-level `baselinedAccounts` set so it persists
  // across the unmount/remount caused by a fullscreen practice session.
  const lastAccountRef = useRef<string | null>(null);
  // Only run for learners
  const isLearner = activeAccount?.role === 'learner';
  const accountId = activeAccount?.id ?? null;
  // Calculate stats needed for achievements
  const {
    masteredCount,
    totalQuestionsAnswered
  } = useMemo(() => {
    if (!isLearner || !state) return {
      masteredCount: 0,
      totalQuestionsAnswered: 0
    };
    let mastered = 0;
    let totalQs = 0;
    MODULES.forEach(m => {
      const mastery = getModuleMasteryForGroup(m.id);
      const answered = getAnsweredQuestions(m.id).length;
      totalQs += answered;
      if (mastery >= MODULE_MASTERED_THRESHOLD) mastered++;
    });
    return {
      masteredCount: mastered,
      totalQuestionsAnswered: totalQs
    };
  }, [state, isLearner, getModuleMasteryForGroup, getAnsweredQuestions]);
  // Derive specific values to avoid re-running on every state change
  const level = state?.stats?.level ?? 0;
  const xp = state?.stats?.xp ?? 0;
  const unlockedCount = state?.stats?.unlockedAchievements?.length ?? 0;
  const currentWeekDays = state?.dailyStreak?.currentWeekDays || 0;
  const consecutiveFullWeeks = state?.dailyStreak?.consecutiveFullWeeks || 0;
  const longestTotalStreak = state?.streak?.longestTotalStreak || 0;
  const totalDailyGoalsReached = state?.dailyStreak?.totalDailyGoalsReached ?? (state?.dailyStreak?.weeklyHistory ? Object.values(state.dailyStreak.weeklyHistory).reduce((a, b) => a + b, 0) : 0);
  const totalPerfectSessions = state?.dailyStreak?.totalPerfectSessions ?? 0;
  // Clear any in-flight celebration queue when the active account changes.
  useEffect(() => {
    if (accountId !== lastAccountRef.current) {
      setQueue([]);
      setActiveAchievement(null);
    }
  }, [accountId]);
  useEffect(() => {
    if (!isLearner || !state) return;
    const checkData: AchievementCheckData = {
      totalQuestionsAnswered,
      masteredCount,
      level,
      xp,
      currentWeekDays,
      consecutiveFullWeeks,
      longestTotalStreak,
      totalDailyGoalsReached,
      totalPerfectSessions
    };
    const unlockedIds = state.stats.unlockedAchievements || [];
    // Determine if this is the first check for this account (baseline run).
    // Keyed off the module-level ledger so a remount after a fullscreen
    // practice session does NOT re-baseline (which would silently swallow an
    // achievement the learner just crossed mid-session, e.g. level 10).
    const isBaselineRun = accountId !== null && !baselinedAccounts.has(accountId);
    const newlyUnlocked: AchievementData[] = [];
    ACHIEVEMENTS.forEach(achievement => {
      if (!unlockedIds.includes(achievement.id) && achievement.check(checkData)) {
        // Mark as unlocked immediately to prevent multiple triggers
        markAchievementUnlocked(achievement.id);
        // Only queue modal if this is NOT the baseline run
        // (baseline = first check after account load/switch — silently mark existing achievements)
        if (!isBaselineRun) {
          newlyUnlocked.push({
            id: achievement.id,
            name: achievement.name,
            description: achievement.description,
            emoji: achievement.emoji,
            rarity: achievement.rarity
          });
        }
      }
    });
    // Mark baseline as done for this account (module-level, survives remount).
    if (isBaselineRun && accountId !== null) {
      baselinedAccounts.add(accountId);
    }
    lastAccountRef.current = accountId;
    if (newlyUnlocked.length > 0) {
      setQueue(prev => [...prev, ...newlyUnlocked]);
    }
  }, [isLearner, accountId, totalQuestionsAnswered, masteredCount, level, xp, unlockedCount, currentWeekDays, consecutiveFullWeeks, longestTotalStreak, totalDailyGoalsReached, totalPerfectSessions, markAchievementUnlocked]);
  // Process queue
  useEffect(() => {
    if (!activeAchievement && queue.length > 0) {
      const next = queue[0];
      setActiveAchievement(next);
      setQueue(prev => prev.slice(1));
      // If this achievement grants a skin, remember it so the cinematic
      // evolution can fire once the badge modal closes. `from` is captured now
      // (before any in-modal equip) for the strongest visual contrast.
      const grantsVariant = ACHIEVEMENTS.find(a => a.id === next.id)?.djayVariantUnlocked;
      pendingSkinRevealRef.current = grantsVariant ? {
        variant: grantsVariant,
        from: selectedVariant
      } : null;
      // Fold any pending level-up into this (the first) celebration modal.
      const pending = state?.pendingLevelUp;
      if (pending) setLevelUpTo(pending);
    }
  }, [queue, activeAchievement, state?.pendingLevelUp, selectedVariant]);
  const handleClose = () => {
    // Clear the level-up only once the combined modal is dismissed, so the
    // XP-bar shockwave and this modal don't double-celebrate the same level.
    if (levelUpTo) {
      clearPendingLevelUp();
      setLevelUpTo(null);
    }
    setActiveAchievement(null);
    // Follow-up beat: if this achievement granted a skin, evolve Djay into it.
    const skin = pendingSkinRevealRef.current;
    pendingSkinRevealRef.current = null;
    if (skin) {
      triggerSkinReveal({
        variant: skin.variant,
        from: skin.from
      });
    }
  };
  return <AchievementUnlockModal achievement={activeAchievement} onClose={handleClose} levelUpTo={levelUpTo} onNavigateToCollection={onNavigateToCollection ? () => {
    if (levelUpTo) {
      clearPendingLevelUp();
      setLevelUpTo(null);
    }
    // Navigating to the Collection IS the wayfinding payoff, so skip
    // the in-place evolution cinematic here — just drop the pending
    // reveal so it doesn't fire on a later close.
    pendingSkinRevealRef.current = null;
    setActiveAchievement(null);
    onNavigateToCollection();
  } : undefined} data-id="element-2250" data-mp="3u3h4xyp8pabu" />;
}